/** @format */

import { TGiornoPresenze } from "../../../redux-modules/declarations/maps/presenzeVista";
import {
  assenzaDelGiorno,
  eStraordinario,
  livelloPresenza,
  minutiDalGiorno,
  ora,
  orario,
  soloRiposo,
  testoGiustificativo,
  tintaPiena,
  TRATTINO,
} from "./format";

/**
 * Blocchi della linea oraria di un giorno, senza interfaccia: posizioni in percentuale sulla
 * fascia mostrata, etichette accorciate in base allo spazio stimato in pixel.
 */

export interface TFasciaMinuti {
  da: number;
  a: number;
}

export interface TBloccoLinea {
  chiave: string;
  tipo: "turno" | "permesso" | "presenza";
  classe: string;
  sinistra: number;
  larghezza: number;
  etichetta: string;
  titolo: string;
  viva: boolean;
  fondo?: string;
  bordo?: string;
}

export interface TFuoriFascia {
  lato: "sinistra" | "destra";
  etichetta: string;
  titolo: string;
}

export interface TLineaGiorno {
  blocchi: TBloccoLinea[];
  fuori: TFuoriFascia[];
  intera: {
    classe: string;
    etichetta: string;
    colore: string | null;
    inAttesa: boolean;
    /** inizio in percentuale: dopo la notte prima, se c'è una presenza o un turno che ne arriva */
    sinistra: number;
  } | null;
  /** percentuale della linea "adesso", solo nel giorno di oggi */
  adesso: number | null;
}

interface TPosizione {
  sinistra: number;
  larghezza: number;
  pixel: number;
  dentro: boolean;
  primaDellaFascia: boolean;
}

const posizione = (
  inizio: number,
  fine: number,
  fascia: TFasciaMinuti,
  pixel: number,
): TPosizione => {
  const ampiezza = fascia.a - fascia.da;
  const a = Math.max(inizio, fascia.da);
  const b = Math.min(fine, fascia.a);
  return {
    sinistra: ((a - fascia.da) / ampiezza) * 100,
    larghezza: ((b - a) / ampiezza) * 100,
    pixel: ((b - a) / ampiezza) * pixel,
    dentro: b > a,
    primaDellaFascia: inizio < fascia.da,
  };
};

/** Il primo testo che entra nello spazio, dal più lungo al più corto. */
const accorcia = (pixel: number, testi: [number, string][]): string => {
  for (const [minimo, testo] of testi) {
    if (pixel >= minimo) return testo;
  }
  return "";
};

export const lineaDelGiorno = (
  giorno: TGiornoPresenze,
  fascia: TFasciaMinuti,
  adesso: string | null,
  pixel: number,
): TLineaGiorno => {
  const blocchi: TBloccoLinea[] = [];
  const prima: { fine: number; titolo: string }[] = [];
  const dopo: { inizio: number; titolo: string }[] = [];
  const minuti = (dataOra: string): number => minutiDalGiorno(dataOra, giorno.data);
  const adessoMinuti = adesso ? minuti(adesso) : null;

  const fuoriFascia = (inizio: number, fine: number, titolo: string): boolean => {
    if (fine <= fascia.da) {
      prima.push({ fine, titolo });
      return true;
    }
    if (inizio >= fascia.a) {
      dopo.push({ inizio, titolo });
      return true;
    }
    return false;
  };

  // fine della notte prima: turni e presenze iniziati il giorno prima che arrivano nel giorno
  const fineNotte = Math.max(
    0,
    ...giorno.turni.filter(t => t.giorno_prima && t.lavorativo).map(t => minuti(t.fine)),
    ...giorno.presenze
      .filter(p => p.giorno_prima)
      .map(p => (p.uscita ? minuti(p.uscita) : adessoMinuti ?? 0)),
  );
  const sinistraIntera =
    fineNotte > fascia.da
      ? ((Math.min(fineNotte, fascia.a) - fascia.da) / (fascia.a - fascia.da)) * 100
      : 0;

  // assenza di tutto il giorno o riposo: un solo blocco al posto della linea, dopo la notte prima
  const assenza = assenzaDelGiorno(giorno);
  let intera: TLineaGiorno["intera"] = null;
  if (assenza) {
    intera = {
      classe: assenza.stato === "in_attesa" ? "in-attesa" : "",
      etichetta: `${assenza.nome}, tutto il giorno${
        assenza.stato === "in_attesa" ? ", in attesa" : ""
      }`,
      colore: assenza.colore,
      inAttesa: assenza.stato === "in_attesa",
      sinistra: sinistraIntera,
    };
  } else if (soloRiposo(giorno)) {
    const recupero = giorno.turni.some(t => t.codice_sistema === "RECUPERO_BANCA_ORE");
    intera = {
      classe: "riposo",
      etichetta: recupero ? "Recupero" : "Riposo",
      colore: null,
      inAttesa: false,
      sinistra: sinistraIntera,
    };
  }

  giorno.turni
    // con l'assenza di tutto il giorno restano a vista solo i turni della notte prima
    .filter(t => t.lavorativo && t.visibile && (!intera || t.giorno_prima))
    .forEach(t => {
      const inizio = minuti(t.inizio);
      const fine = minuti(t.fine);
      const fascia_ = orario(t.ora_inizio, t.ora_fine);
      const nonTimbrato = t.stato === "mancata_timbratura";
      const coperto = t.stato === "coperto";
      const titolo = nonTimbrato
        ? `Turno non timbrato ${fascia_}`
        : coperto
        ? `Turno ${fascia_} coperto da ${
            t.coperto_da === "timbratura_singola" ? "timbratura singola" : "giustificativo"
          }`
        : `Turno previsto ${fascia_}`;
      if (fuoriFascia(inizio, fine, titolo)) return;
      const pos = posizione(inizio, fine, fascia, pixel);
      blocchi.push({
        chiave: `t${t.id}`,
        tipo: "turno",
        classe: `blocco turno${nonTimbrato ? " non-timbrato" : ""}${coperto ? " coperto" : ""}`,
        sinistra: pos.sinistra,
        larghezza: pos.larghezza,
        etichetta: nonTimbrato
          ? accorcia(pos.pixel, [
              [150, `Non timbrato ${fascia_}`],
              [80, "Non timbrato"],
              [40, t.ora_inizio],
            ])
          : accorcia(pos.pixel, [
              [80, fascia_],
              [40, t.ora_inizio],
            ]),
        titolo,
        viva: false,
      });
    });

  giorno.giustificativi
    .filter(g => g.unita === "ORE" && g.dalle && g.alle)
    .forEach(g => {
      // con data e ora: la parte dopo mezzanotte di un notturno sta oltre le 24 del giorno del turno
      // (ROL 00:00–01:00 del 24 sul turno 16:00–01:00 del 23); con un backend vecchio solo le ore del giorno
      const inizio = minuti(g.inizio || `${giorno.data} ${g.dalle}`);
      let fine = minuti(g.fine || `${giorno.data} ${g.alle}`);
      if (fine <= inizio) fine += 1440;
      if (intera && fine > fineNotte) return;
      const inAttesa = g.stato === "in_attesa";
      const testo = `${testoGiustificativo(g, giorno.data)}${inAttesa ? ", in attesa" : ""}`;
      if (fuoriFascia(inizio, fine, testo)) return;
      const pos = posizione(inizio, fine, fascia, pixel);
      blocchi.push({
        chiave: `g${g.richiesta_id}`,
        tipo: "permesso",
        classe: `blocco permesso${inAttesa ? " in-attesa" : ""}`,
        sinistra: pos.sinistra,
        larghezza: pos.larghezza,
        etichetta: accorcia(pos.pixel, [
          [140, testo],
          [50, g.nome],
        ]),
        titolo: testo,
        viva: false,
        // colore pieno: copre il turno che sta sotto e la sua scritta
        fondo: tintaPiena(g.colore, 0.45),
        bordo: tintaPiena(g.colore, 0.9),
      });
    });

  giorno.presenze.forEach(p => {
    const inizio = minuti(p.entrata);
    const fine = p.uscita
      ? minuti(p.uscita)
      : adessoMinuti !== null
      ? Math.max(adessoMinuti, inizio)
      : inizio;
    const livello = livelloPresenza(giorno, p.id);
    const da = p.giorno_prima ? `${ora(p.entrata)} del giorno prima` : ora(p.entrata);
    const a = p.uscita
      ? p.giorno_dopo
        ? `${ora(p.uscita)} del giorno dopo`
        : ora(p.uscita)
      : "in corso";
    const titolo = `${eStraordinario(p) ? "Straordinario" : "Timbratura"} ${da} ${TRATTINO} ${a}`;
    if (fuoriFascia(inizio, Math.max(fine, inizio + 1), titolo)) return;
    const pos = posizione(inizio, Math.max(fine, inizio + 1), fascia, pixel);
    const tagliata = pos.primaDellaFascia ? "‹ " : "";
    const finale = p.uscita ? ora(p.uscita) : "in corso";
    blocchi.push({
      chiave: `p${p.id}`,
      tipo: "presenza",
      classe: `blocco presenza ${
        livello || (eStraordinario(p) ? "straordinario" : p.aperta ? "aperta" : "chiusa")
      }`,
      sinistra: pos.sinistra,
      larghezza: Math.max(pos.larghezza, 0.4),
      etichetta: tagliata
        ? accorcia(pos.pixel, [[40, `${tagliata}${finale}`]])
        : accorcia(pos.pixel, [
            [130, `${ora(p.entrata)} ${TRATTINO} ${finale}`],
            [90, p.aperta ? ora(p.entrata) : `${ora(p.entrata)}${TRATTINO}${ora(p.uscita)}`],
            [40, ora(p.entrata)],
          ]),
      titolo,
      viva: p.aperta,
    });
  });

  const fuori: TFuoriFascia[] = [];
  if (prima.length) {
    const ultima = Math.max(...prima.map(x => x.fine));
    fuori.push({
      lato: "sinistra",
      etichetta: `‹ ${orarioMinuti(ultima)}`,
      titolo: `Prima della fascia mostrata: ${prima.map(x => x.titolo).join("; ")}`,
    });
  }
  if (dopo.length) {
    const prima_ = Math.min(...dopo.map(x => x.inizio));
    fuori.push({
      lato: "destra",
      etichetta: `${orarioMinuti(prima_)} ›`,
      titolo: `Dopo la fascia mostrata: ${dopo.map(x => x.titolo).join("; ")}`,
    });
  }

  const adessoPercentuale =
    adesso && adesso.substr(0, 10) === giorno.data && adessoMinuti !== null
      ? adessoMinuti >= fascia.da && adessoMinuti <= fascia.a
        ? ((adessoMinuti - fascia.da) / (fascia.a - fascia.da)) * 100
        : null
      : null;

  return { blocchi, fuori, intera, adesso: adessoPercentuale };
};

/** Minuti dalla mezzanotte in "HH:mm" (anche del giorno prima o dopo). */
export const orarioMinuti = (minuti: number): string => {
  const v = ((minuti % 1440) + 1440) % 1440;
  const h = Math.floor(v / 60);
  const m = v % 60;
  return `${h < 10 ? "0" : ""}${h}:${m < 10 ? "0" : ""}${m}`;
};

/** Ore dell'asse: etichette e posizioni. */
export const oreAsse = (
  fascia: TFasciaMinuti,
  passo: number,
): { etichetta: string; sinistra: number }[] => {
  const ore: { etichetta: string; sinistra: number }[] = [];
  for (let h = fascia.da / 60; h <= fascia.a / 60; h += passo) {
    ore.push({
      etichetta: `${h < 10 ? "0" : ""}${h}`,
      sinistra: ((h * 60 - fascia.da) / (fascia.a - fascia.da)) * 100,
    });
  }
  return ore;
};

/** Griglia delle ore sullo sfondo della linea. */
export const sfondoOre = (fascia: TFasciaMinuti): string =>
  `${(100 / ((fascia.a - fascia.da) / 60)).toFixed(4)}% 100%`;
